/**
 * Reusable image uploader backed by object storage (Cloudflare R2 via the API).
 *
 * Uploads the chosen file straight to the bucket and reports back the public
 * URL through `onChange`. Store that URL string on your record (e.g. a
 * `logo_url`, `photo_url`, or attachment field) — the bytes live in the
 * bucket, never on the server's disk.
 *
 * Usage:
 *   <ImageUploader folder="users" value={photoUrl} onChange={setPhotoUrl} />
 */
import { useState } from 'react'
import { Upload, message } from 'antd'
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import type { UploadFolder } from '@/services/api'
import { api } from '@/services/api'

interface Props {
  folder: UploadFolder
  value?: string | null
  onChange?: (url: string | null) => void
  /** Show the preview as a round avatar (good for profile photos). */
  shape?: 'square' | 'round'
}

export default function ImageUploader({ folder, value, onChange, shape = 'square' }: Props) {
  const [loading, setLoading] = useState(false)

  const handleUpload = async (file: File) => {
    setLoading(true)
    try {
      const { url } = await api.uploadImage(folder, file)
      onChange?.(url)
      message.success('Image uploaded')
    } catch (err: any) {
      message.error(err?.message || 'Upload failed')
    } finally {
      setLoading(false)
    }
    return false // stop antd from doing its own request
  }

  return (
    <Upload
      name="file"
      listType="picture-card"
      showUploadList={false}
      accept="image/*"
      beforeUpload={(file) => handleUpload(file as unknown as File)}
    >
      {value ? (
        <img
          src={value}
          alt="upload"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: shape === 'round' ? '50%' : 8,
          }}
        />
      ) : (
        <div>
          {loading ? <LoadingOutlined /> : <PlusOutlined />}
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      )}
    </Upload>
  )
}
